import { api } from '../apiMethods'
import { API_ENDPOINTS } from '@/data/constants'
import type {
  AuthMeResponse,
  AuthMeWithTeamsResponse,
  UserTeamsResponse,
  SetTeamContextRequest,
  SetTeamContextResponse,
  ValidateTeamContextResponse,
  PermissionsResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
} from '@/types/auth'

export const authApi = {
  login: (credentials: { email: string; password: string }) => {
    return api.post(API_ENDPOINTS.AUTH.LOGIN, credentials)
  },

  register: (userData: { email: string; password: string; name: string }) => {
    return api.post(API_ENDPOINTS.AUTH.REGISTER, userData)
  },

  logout: () => {
    return api.post(API_ENDPOINTS.AUTH.LOGOUT)
  },

  refresh: (refreshToken: string) => {
    return api.post(API_ENDPOINTS.AUTH.REFRESH, { refreshToken })
  },

  verify: (token: string) => {
    return api.post(API_ENDPOINTS.AUTH.VERIFY, { token })
  },

  resetPassword: (token: string, password: string, confirmPassword: string) => {
    return api.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, { token, password, confirmPassword })
  },

  forgotPassword: (email: string) => {
    return api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email })
  },

  acceptInvitation: (data: AcceptInvitationRequest) => {
    return api.post<AcceptInvitationResponse>(API_ENDPOINTS.AUTH.ACCEPT_INVITATION, data)
  },

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string
  }) => {
    return api.put(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, data)
  },

  // ========================================
  // RBAC Endpoints
  // ========================================

  /**
   * Get current user profile
   */
  getMe: () => {
    return api.get<AuthMeResponse>('/auth/me')
  },

  /**
   * Get current user with teams
   */
  getMeWithTeams: () => {
    return api.get<AuthMeWithTeamsResponse>('/auth/me/teams')
  },

  /**
   * Get all teams for current user
   */
  getTeams: () => {
    return api.get<UserTeamsResponse>('/auth/teams')
  },

  /**
   * Set the active team context
   */
  setTeamContext: (data: SetTeamContextRequest) => {
    return api.post<SetTeamContextResponse>('/auth/set-team-context', data)
  },

  /**
   * Validate current team context
   */
  validateTeamContext: (teamId: string) => {
    return api.get<ValidateTeamContextResponse>(`/auth/validate-team-context?team_id=${teamId}`)
  },

  /**
   * Get user permissions for a specific team
   */
  getPermissions: (teamId: string) => {
    return api.get<PermissionsResponse>(`/auth/permissions?team_id=${teamId}`)
  },

  getToken: () => {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const authData = JSON.parse(authStorage)
        return authData.state?.token || null
      } catch (error) {
        console.error('Error parsing auth storage:', error)
        return null
      }
    }
    return null
  },

  /**
   * Get current team ID from storage
   */
  getCurrentTeamId: () => {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const authData = JSON.parse(authStorage)
        return authData.state?.currentTeamId || null
      } catch (error) {
        console.error('Error parsing auth storage:', error)
        return null
      }
    }
    return null
  },
}